import { CheckCircleFilled, ClockCircleOutlined, InfoCircleOutlined, ProjectOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { ArchiveBox } from '../../assets/CustomIcons/ArchiveBox';
import { BacklogCircle } from '../../assets/CustomIcons/BacklogCircle';
import { ExpandDown } from '../../assets/CustomIcons/ExpandDown';
import { SprintIcon } from '../../assets/CustomIcons/SprintIcon';
import { TriageTriangle } from '../../assets/CustomIcons/TriageTriangle';
import colorTokens from '../../constants/design_system/colors';
import { WorkspaceDefaultModule, WorkspaceFramework } from '../../types/Workspace';
import { getDefaultModuleDisplayName } from '../../utils/workspaceUtils';
import BaseDropdown from '../UI/Dropdown/BaseDropdown';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';

const getModuleIcon = (module: WorkspaceDefaultModule) => {
  switch (module) {
    case WorkspaceDefaultModule.triage:
      return <TriageTriangle />;
    case WorkspaceDefaultModule.backlog:
      return <BacklogCircle />;
    case WorkspaceDefaultModule.planning:
      return <ClockCircleOutlined />;
    case WorkspaceDefaultModule.sprint:
      return <SprintIcon />;
    case WorkspaceDefaultModule.kanban:
      return <ProjectOutlined />;
    case WorkspaceDefaultModule.archive:
      return <ArchiveBox />;
  }
};

export type FrameworkCardProps = {
  framework: WorkspaceFramework;
  selected: boolean;
  current: boolean;
  onSelected: (framework: WorkspaceFramework) => void;
  defaultModule?: WorkspaceDefaultModule;
  onDefaultModuleChange?: (module: WorkspaceDefaultModule) => void;
  defaultModuleOptions?: WorkspaceDefaultModule[];
};

export const FrameworkCard = (props: FrameworkCardProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (props.framework === WorkspaceFramework.none) {
    return (
      <div className="flex items-center gap-2 mt-4 cursor-pointer" onClick={() => props.onSelected(props.framework)}>
        {props.selected && <CheckCircleFilled style={{ fontSize: '16px' }} className="text-blue-500" />}
        {!props.selected && <div className="w-[14px] h-[14px] rounded-lg border border-solid border-neutral-600" />}
        <div className="text-neutral-900 text-sm font-medium leading-tight">Go without a framework</div>
        {props.current && <div className="text-neutral-800 text-xs font-normal leading-none">(current)</div>}
      </div>
    );
  }

  const title = props.framework === WorkspaceFramework.scrum ? 'Sprint' : 'Kanban';
  const subtitle =
    props.framework === WorkspaceFramework.scrum
      ? 'Best for teams who manage epics and tasks through planning and sprint phases.'
      : 'Most used by teams who need to track continuous task delivery.';
  const icon =
    props.framework === WorkspaceFramework.scrum ? (
      <SprintIcon className="text-neutral-900" />
    ) : (
      <ProjectOutlined className="text-neutral-900" />
    );

  return (
    <div
      style={{ borderColor: props.selected ? colorTokens.blue[600] : colorTokens.neutral[300] }}
      className="px-4 py-3 rounded-lg border-solid border-2 border-blue-600 flex-col justify-start items-start gap-3 grow basis-1/2 cursor-pointer"
      onClick={() => props.onSelected(props.framework)}
    >
      <div className="w-full justify-between items-center inline-flex">
        <div className="justify-start items-center gap-2 flex">
          {icon}
          <div className="flex items-center gap-1 text-brand-grey-dark text-base font-semibold leading-normal">
            {title}
            {props.current && <div className="text-neutral-800 text-xs font-normal leading-none">(current)</div>}
          </div>
        </div>
        {props.selected && <CheckCircleFilled style={{ fontSize: '16px' }} className="text-blue-500" />}
        {!props.selected && <div className="w-[14px] h-[14px] rounded-lg border border-solid border-neutral-600" />}
      </div>
      <div className="self-stretch text-neutral-900 text-xs font-normal leading-none">{subtitle}</div>

      {props.selected && props.defaultModuleOptions && props.defaultModuleOptions.length > 0 && (
        <div className="flex items-center justify-between mt-3" onClick={(e) => e.stopPropagation()}>
          <CustomTooltip title="Default Module determines which page the Teamspace members will land on">
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 text-xs font-medium">Default Module</span>
              <InfoCircleOutlined className="text-neutral-800 text-xs cursor-pointer" />
            </div>
          </CustomTooltip>

          <BaseDropdown
            trigger={['click']}
            open={isDropdownOpen}
            onOpenChange={(open) => setIsDropdownOpen(open)}
            blockScroll={false}
            preventClickThrough={false}
            placement="bottomLeft"
            dropdownRender={() => (
              <div className="flex flex-col items-start bg-brand-white shadow-md rounded-lg py-2">
                {props.defaultModuleOptions!.map((option) => (
                  <div
                    key={option}
                    className="w-full flex items-center gap-2 py-1.5 rounded cursor-pointer hover:bg-neutral-200"
                    onClick={() => {
                      props.onDefaultModuleChange?.(option);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span className="text-neutral-900 text-sm flex items-center justify-center pl-2">{getModuleIcon(option)}</span>
                    <span className="text-neutral-900 text-xs font-normal pr-2">{getDefaultModuleDisplayName(option)}</span>
                  </div>
                ))}
              </div>
            )}
          >
            <div className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-neutral-200 cursor-pointer">
              <span className="text-neutral-900 text-sm flex items-center justify-center">{props.defaultModule && getModuleIcon(props.defaultModule)}</span>
              <span className="text-neutral-900 text-xs font-medium">
                {props.defaultModule && getDefaultModuleDisplayName(props.defaultModule)}
              </span>
              <ExpandDown className="text-neutral-800 text-xs" />
            </div>
          </BaseDropdown>
        </div>
      )}
    </div>
  );
};
